import React from 'react';
import { useOfficerDashboard } from '../../hooks/useDashboard';
import { KPICard } from '../../components/dashboard/KPICard';
import { Card } from '../../components/ui/Card';
import { Badge, PriorityBadge, StatusBadge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { AlertTriangle, Clock, Building2, CheckCircle2 } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/Feedback';

export const OfficerDashboardPage: React.FC = () => {
  const { data, loading, error, filter, setFilter } = useOfficerDashboard();

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-rose-400 text-sm p-4">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <FilterBar filter={filter} onChange={setFilter} showStatus showPriority />
        <ExportButton
          config={{
            filename: 'officer-dashboard',
            sheetName: 'Officer Dashboard',
            columns: [
              { key: 'complaint_id', header: 'Complaint ID', width: 20 },
              { key: 'title', header: 'Title', width: 30 },
              { key: 'priority', header: 'Priority', width: 12 },
              { key: 'time_remaining', header: 'Time Remaining', width: 15 },
              { key: 'status', header: 'Status', width: 15 },
            ],
            data: data.today_priorities,
            filters: filter,
            generatedAt: new Date().toISOString(),
            generatedBy: 'Officer',
          }}
        />
      </div>

      <DashboardGrid columns={4}>
        {data.kpis.map((kpi, i) => (
          <KPICard key={i} metric={kpi} />
        ))}
      </DashboardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Today's Priorities" subtitle="Urgent items requiring attention" className="lg:col-span-2">
          <div className="space-y-3">
            {data.today_priorities.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-sky-400 font-semibold">{item.complaint_id}</span>
                    <PriorityBadge priority={item.priority} />
                  </div>
                  <p className="text-sm text-slate-200 mt-1 truncate">{item.title}</p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className="text-xs text-slate-400 whitespace-nowrap">{item.time_remaining}</span>
                  <StatusBadge status={item.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Vendor Load" subtitle="Current workload distribution">
          <div className="space-y-3">
            {data.vendor_load.map((vendor, i) => (
              <div key={i} className="p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
                <p className="text-sm font-medium text-slate-200">{vendor.vendor_name}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-sky-400">{vendor.active_orders}</p>
                    <p className="text-[10px] text-slate-500">Active</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-emerald-400">{vendor.completed_orders}</p>
                    <p className="text-[10px] text-slate-500">Completed</p>
                  </div>
                </div>
                <div className="mt-2">
                  <Badge
                    label={vendor.load_status}
                    variant={
                      vendor.load_status === 'balanced' ? 'success' :
                      vendor.load_status === 'overloaded' ? 'danger' : 'info'
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};
